import { useState, useEffect, useContext } from "react";
import { Helmet } from "react-helmet-async";
import { CircularProgress, Tabs, Tab } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import {
  IconBrandYoutube,
  IconExternalLink,
  IconSearch,
  IconMail,
} from "@tabler/icons-react";
import CustomButton from "../utils/customButton";
import Functions from "../../callable/functions";
import { youtube_v3 } from "googleapis";
import { UserContext } from "../../contexts/userContext";
import { extractChannelId } from "../../utils/youtubeUtils";
import VideoDetailsCard from "../insighter/VideoDetailsCard";
import SentimentAnalysisPanel from "../insighter/SentimentAnalysisPanel";
import AudienceInsightsPanel from "../insighter/AudienceInsightsPanel";
import VideoGrid from "../insighter/VideoGrid";
import { Lead } from "../../models/lead";
import moment from "moment";
import LeadDb from "../../db/leadDb";
import { addDoc, getDocs, query, where } from "firebase/firestore";

export default function ChannelInsighter() {
  const user = useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [channelUrl, setChannelUrl] = useState("");
  const [channelId, setChannelId] = useState<string | null>(
    searchParams.get("channelId")
  );
  const [channelInfo, setChannelInfo] = useState<any | null>(null);
  const [videos, setVideos] = useState<youtube_v3.Schema$Video[]>([]);
  const [selectedVideo, setSelectedVideo] =
    useState<youtube_v3.Schema$Video | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("latest-videos");
  const [error, setError] = useState<string | null>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [sentimentData, setSentimentData] = useState<any | null>(null);
  const [analyzingSentiment, setAnalyzingSentiment] = useState(false);
  const [analyzingAudience, setAnalyzingAudience] = useState(false);
  const [audienceInsights, setAudienceInsights] = useState<string | null>(null);

  // Use two separate states for email collection:
  // leadEmailInput holds what the user is typing into the email field,
  // leadSubmitted tracks whether the email has been submitted.
  const [leadEmailInput, setLeadEmailInput] = useState("");
  const [leadSubmitted, setLeadSubmitted] = useState(false);

  const pageTitle =
    "YouTube Channel Insighter | Free YouTube Channel Analysis Tool";
  const pageDescription =
    "Analyze any YouTube channel to get insights on their latest videos, performance metrics, audience engagement, and content strategy.";

  useEffect(() => {
    if (channelId) {
      handleFetchChannelVideos(channelId);
    }
  }, [channelId]);

  const handleUrlSubmit = () => {
    const extractedId = extractChannelId(channelUrl);
    if (extractedId) {
      setChannelId(extractedId);
      setSearchParams({ channelId: extractedId });
    } else {
      setError(
        "Please enter a valid YouTube channel URL in the format: https://www.youtube.com/channel/CHANNEL_ID"
      );
    }
  };

  const handleFetchChannelVideos = async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      const functions = new Functions();
      const result = await functions.fetchLatestVideos({
        userId: user?.id || "guest",
        channelId: id,
        maxResults: 20,
      });

      if (result.data.videos && result.data.videos.length > 0) {
        setVideos(result.data.videos);
        setSelectedVideo(result.data.videos[0]); // Select the first video by default

        // Extract channel info from the first video
        if (result.data.videos[0].snippet?.channelTitle) {
          setChannelInfo({
            title: result.data.videos[0].snippet.channelTitle,
            id: result.data.videos[0].snippet.channelId,
            thumbnail: result.data.videos[0].snippet.thumbnails?.default?.url,
          });
        }
      } else {
        setError(
          "No videos found for this channel or we couldn't access this channel."
        );
      }
    } catch (error) {
      console.error("Error fetching channel videos:", error);
      setError(
        "An error occurred while fetching channel videos. Please make sure the channel ID is correct."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSentimentAnalysis = async () => {
    if (!selectedVideo || !selectedVideo.id) return;

    try {
      setAnalyzingSentiment(true);
      setSentimentData(null);

      const functions = new Functions();
      const result = await functions.analyzeCommentsSentiment({
        videoId: selectedVideo.id,
      });

      if (result.data && result.data.sentiment) {
        setSentimentData(result.data.sentiment);
      }
    } catch (error) {
      console.error("Error analyzing sentiment:", error);
      setError(
        "Failed to analyze sentiment. Try signing in for full features."
      );
    } finally {
      setAnalyzingSentiment(false);
    }
  };

  const handleAudienceInsights = async () => {
    if (!selectedVideo || !selectedVideo.id) return;

    try {
      setAnalyzingAudience(true);
      setAudienceInsights(null);

      // Use a fixed prompt for the free tool
      const fixedPrompt =
        "Summarize the main themes in these comments and what viewers are saying about this video.";

      const functions = new Functions();
      const result = await functions.analyzeVideoComments({
        videoId: selectedVideo.id,
        prompt: fixedPrompt,
      });

      if (result.data && result.data.analysis) {
        setAudienceInsights(result.data.analysis);
      }
    } catch (error) {
      console.error("Error analyzing audience insights:", error);
      setError(
        "Failed to analyze audience insights. Try signing in for full features."
      );
    } finally {
      setAnalyzingAudience(false);
    }
  };

  const handleLeadSubmission = async (email: string) => {
    try {
      const leadDb = new LeadDb();
      const leadsRef = leadDb.collection({ leadId: "" });
      // Query to check if this email is already saved.
      const q = query(leadsRef, where("email", "==", email));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        // If the email exists, mark it as submitted without saving a duplicate.
        setLeadSubmitted(true);
        return;
      }
      const lead = new Lead({
        id: "",
        email,
        createdAt: moment(),
      });
      await addDoc(leadsRef, lead);
      setLeadSubmitted(true);
      setError(null);
    } catch (error) {
      console.error("Error saving lead:", error);
      setError("Failed to save your email. Please try again.");
    }
  };

  // Calculate basic performance metrics for a video
  const calculatePerformanceMetrics = (video: youtube_v3.Schema$Video) => {
    if (!video || !video.statistics) return null;

    const views = parseInt(video.statistics?.viewCount || "0", 10);
    const likes = parseInt(video.statistics?.likeCount || "0", 10);
    const comments = parseInt(video.statistics?.commentCount || "0", 10);

    // Extract duration from contentDetails
    const duration = video.contentDetails?.duration || "PT0M0S";
    const durationInMinutes = (() => {
      const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
      if (match) {
        const hours = parseInt(match[1] || "0", 10);
        const minutes = parseInt(match[2] || "0", 10);
        const seconds = parseInt(match[3] || "0", 10);
        return hours * 60 + minutes + seconds / 60;
      }
      return 0;
    })();

    const engagementPerMinute =
      durationInMinutes > 0
        ? ((likes + comments) / durationInMinutes).toFixed(2)
        : "0";
    const engagementRate =
      views > 0 ? (((likes + comments) / views) * 100).toFixed(2) : "0";

    return { views, likes, comments, engagementPerMinute, engagementRate };
  };

  // Render different tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case "latest-videos":
        return (
          <VideoGrid
            videos={videos}
            selectedVideoId={selectedVideo?.id || null}
            onSelectVideo={setSelectedVideo}
          />
        );
      case "video-analysis":
        return renderVideoAnalysis();
      default:
        return (
          <VideoGrid
            videos={videos}
            selectedVideoId={selectedVideo?.id || null}
            onSelectVideo={setSelectedVideo}
          />
        );
    }
  };

  const renderVideoAnalysis = () => {
    if (!selectedVideo) {
      return (
        <div className="py-6 text-center">
          <p>Please select a video to analyze</p>
        </div>
      );
    }

    if (!leadSubmitted) {
      return (
        <div className="py-6">
          <div className="p-6 border border-gray-700 rounded-lg">
            <h3 className="text-xl font-bold mb-4">
              Get Detailed Channel Analysis
            </h3>
            <p className="text-gray-400 mb-4">
              Enter your email to unlock sentiment analysis and audience
              insights for this channel.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                value={leadEmailInput}
                onChange={(e) => setLeadEmailInput(e.target.value)}
                placeholder="Enter your email address"
                className="flex-1 p-2 rounded-lg bg-background border border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <CustomButton
                onClick={() => {
                  if (!leadEmailInput || !leadEmailInput.includes("@")) {
                    setError("Please enter a valid email address");
                    return;
                  }
                  handleLeadSubmission(leadEmailInput);
                }}
                startIcon={<IconMail size={16} />}
              >
                Get Analysis
              </CustomButton>
            </div>
            {error && <p className="text-error mt-2">{error}</p>}
          </div>
        </div>
      );
    }

    return (
      <div className="py-6">
        <VideoDetailsCard video={selectedVideo} />

        <div className="grid grid-cols-1 gap-8 mt-8">
          <SentimentAnalysisPanel
            analyzing={analyzingSentiment}
            sentimentData={sentimentData}
            onAnalyze={handleSentimentAnalysis}
          />

          <AudienceInsightsPanel
            analyzing={analyzingAudience}
            insights={audienceInsights}
            onAnalyze={handleAudienceInsights}
          />
        </div>
      </div>
    );
  };

  // Helper function to format view counts
  const formatCount = (count: number) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  // Helper function to format duration from ISO 8601
  const formatDuration = (isoDuration: string) => {
    const match = isoDuration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!match) return "0:00";

    const hours = match[1] ? parseInt(match[1], 10) : 0;
    const minutes = match[2] ? parseInt(match[2], 10) : 0;
    const seconds = match[3] ? parseInt(match[3], 10) : 0;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds
        .toString()
        .padStart(2, "0")}`;
    } else {
      return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    }
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
      </Helmet>

      <div className="min-h-screen bg-[#0B0B0F] text-white font-custom">
        <header className="w-full max-w-5xl flex justify-between items-center px-4 py-4 mx-auto">
          <a href="/">
            <img
              src="/images/logo.svg"
              alt="YouGenie Logo"
              className="h-6 sm:h-8 cursor-pointer"
            />
          </a>
        </header>

        <main className="max-w-5xl mx-auto px-4 py-8">
          <h1 className="text-2xl sm:text-4xl font-bold mb-4">
            YouTube Channel Insighter
          </h1>
          <p className="text-gray-400 mb-8">
            Get detailed analytics and insights for any YouTube channel. Analyze
            their latest videos, understand audience sentiment, and discover
            what makes their content successful.
          </p>

          {/* Channel URL Input Section */}
          <div className="bg-[#1A1A1A] rounded-lg p-6 mb-8">
            <h2 className="text-xl font-bold mb-4">
              Analyze a YouTube Channel
            </h2>
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="text"
                value={channelUrl}
                onChange={(e) => setChannelUrl(e.target.value)}
                placeholder="Enter YouTube channel URL (e.g., https://www.youtube.com/channel/UC...)"
                className="flex-1 p-2 rounded-lg bg-background border border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <CustomButton
                onClick={handleUrlSubmit}
                disabled={loading}
                startIcon={<IconSearch size={16} />}
              >
                Analyze Channel
              </CustomButton>
            </div>
            {error && <p className="text-error mt-2">{error}</p>}
          </div>

          {/* Channel Details and Analysis Section */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <CircularProgress color="inherit" size={40} />
            </div>
          ) : channelInfo ? (
            <div className="bg-[#1A1A1A] rounded-lg p-6">
              {/* Channel Info */}
              <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-800">
                {channelInfo.thumbnail ? (
                  <img
                    src={channelInfo.thumbnail}
                    alt={channelInfo.title}
                    className="w-16 h-16 rounded-full"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center">
                    <IconBrandYoutube size={32} className="text-red-500" />
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-bold">{channelInfo.title}</h2>
                  <a
                    href={`https://www.youtube.com/channel/${channelInfo.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white flex items-center mt-1"
                  >
                    View on YouTube{" "}
                    <IconExternalLink size={14} className="ml-1" />
                  </a>
                </div>
              </div>

              {/* Analysis Tabs */}
              <div className="mb-4">
                <Tabs
                  value={activeTab}
                  onChange={(_, newValue) => setActiveTab(newValue)}
                  textColor="inherit"
                  indicatorColor="secondary"
                  className="mb-6"
                  sx={{
                    ".MuiTabs-indicator": {
                      backgroundColor: "#FF0000",
                    },
                    ".MuiTab-root": {
                      textTransform: "none",
                      fontWeight: "bold",
                    },
                  }}
                >
                  <Tab value="latest-videos" label="Latest Videos" />
                  <Tab value="video-analysis" label="Video Analysis" />
                </Tabs>

                {renderTabContent()}
              </div>
            </div>
          ) : (
            <div className="bg-[#1A1A1A] rounded-lg p-6 text-center py-12">
              <IconBrandYoutube
                size={48}
                className="mx-auto mb-4 text-red-500"
              />
              <h3 className="text-xl font-bold mb-2">
                Enter a YouTube Channel URL to get started
              </h3>
              <p className="text-gray-400">
                Analyze any YouTube channel to discover insights about their
                content strategy and audience engagement.
              </p>
            </div>
          )}

          {/* SEO Sections */}
          <section className="mt-16">
            <h2 className="text-3xl font-bold mb-8">
              Benefits of Using Channel Insighter
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-[#1A1A1A] p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3">
                  Complete Channel Overview
                </h3>
                <p className="text-gray-400">
                  Get a comprehensive view of any YouTube channel's latest
                  content, performance metrics, and audience engagement all in
                  one place.
                </p>
              </div>
              <div className="bg-[#1A1A1A] p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3">
                  Compare Video Performance
                </h3>
                <p className="text-gray-400">
                  Easily browse through a channel's recent videos and compare
                  their performance to identify trends and patterns in
                  successful content.
                </p>
              </div>
              <div className="bg-[#1A1A1A] p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3">
                  Content Strategy Insights
                </h3>
                <p className="text-gray-400">
                  Understand what types of content work best for any channel by
                  analyzing audience sentiment and engagement across multiple
                  videos.
                </p>
              </div>
            </div>
          </section>

          <section className="mt-16">
            <h2 className="text-3xl font-bold mb-8">
              How to Use Channel Insighter
            </h2>
            <div className="bg-[#1A1A1A] p-6 rounded-lg">
              <ol className="space-y-4">
                <li className="flex items-start">
                  <span className="bg-primary-500 text-black font-bold rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0">
                    1
                  </span>
                  <div>
                    <h3 className="text-xl font-bold">
                      Enter a YouTube Channel URL
                    </h3>
                    <p className="text-gray-400">
                      Copy and paste the URL of any public YouTube channel you
                      want to analyze.
                    </p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-primary-500 text-black font-bold rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0">
                    2
                  </span>
                  <div>
                    <h3 className="text-xl font-bold">Browse Latest Videos</h3>
                    <p className="text-gray-400">
                      View the channel's most recent videos in the "Latest
                      Videos" tab to see what content they're currently
                      producing.
                    </p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-primary-500 text-black font-bold rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0">
                    3
                  </span>
                  <div>
                    <h3 className="text-xl font-bold">
                      Select a Video to Analyze
                    </h3>
                    <p className="text-gray-400">
                      Click on any video to select it for detailed analysis.
                    </p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="bg-primary-500 text-black font-bold rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0">
                    4
                  </span>
                  <div>
                    <h3 className="text-xl font-bold">Run Video Analysis</h3>
                    <p className="text-gray-400">
                      Switch to the "Video Analysis" tab to see detailed
                      metrics, sentiment analysis, and audience insights for the
                      selected video.
                    </p>
                  </div>
                </li>
              </ol>
            </div>
          </section>

          <section className="mt-16">
            <h2 className="text-3xl font-bold mb-8">Common Use Cases</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-[#1A1A1A] p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3">Content Research</h3>
                <p className="text-gray-400">
                  Research successful channels in your niche to understand what
                  content performs well and how audiences respond to different
                  video types.
                </p>
              </div>
              <div className="bg-[#1A1A1A] p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3">Competitor Analysis</h3>
                <p className="text-gray-400">
                  Track your competitors' content strategies, video performance,
                  and audience sentiment to identify opportunities for your own
                  channel.
                </p>
              </div>
              <div className="bg-[#1A1A1A] p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3">Influencer Research</h3>
                <p className="text-gray-400">
                  Evaluate potential influencer partners by analyzing their
                  channel performance, content quality, and audience engagement
                  before collaboration.
                </p>
              </div>
              <div className="bg-[#1A1A1A] p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3">
                  Channel Growth Strategy
                </h3>
                <p className="text-gray-400">
                  Use insights from successful channels to inform your own
                  content planning and channel growth strategy.
                </p>
              </div>
            </div>
          </section>

          <section className="mt-16 mb-16">
            <h2 className="text-3xl font-bold mb-8">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              <div className="bg-[#1A1A1A] p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-2">
                  Is Channel Insighter free to use?
                </h3>
                <p className="text-gray-400">
                  Yes, the basic version of Channel Insighter is completely free
                  to use. For more advanced features and unlimited channel
                  analysis, consider signing up for our premium plan.
                </p>
              </div>
              <div className="bg-[#1A1A1A] p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-2">
                  How many videos can I analyze from a channel?
                </h3>
                <p className="text-gray-400">
                  The free version displays up to 20 of the most recent videos
                  from a channel. Premium users can access more historical
                  content and run batch analysis on multiple videos.
                </p>
              </div>
              <div className="bg-[#1A1A1A] p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-2">
                  Can I see private channel data?
                </h3>
                <p className="text-gray-400">
                  No, Channel Insighter can only analyze publicly available
                  data. Private videos, hidden subscriber counts, or other
                  restricted information cannot be accessed.
                </p>
              </div>
              <div className="bg-[#1A1A1A] p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-2">
                  How often is the channel data updated?
                </h3>
                <p className="text-gray-400">
                  Channel Insighter fetches the most current data available
                  through YouTube's API each time you analyze a channel. This
                  ensures you're seeing the most up-to-date metrics and videos.
                </p>
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  );
}
